import { useState, useMemo, useRef } from 'react'
import {
  startOfDay,
  format,
  addDays,
  addWeeks,
  addMonths,
  getISOWeek,
  getISOWeekYear,
  startOfISOWeek,
} from 'date-fns'
import { useSimulatorParams, resolveSimRange, resolveSimFetchRange } from '@/hooks/use-simulator-params'
import { PeriodGridPicker } from '@/components/ui/PeriodGridPicker'
import { AlertCircle, ChevronLeft, ChevronRight, HelpCircle, Loader2, Upload, X } from 'lucide-react'
import { InfoTip } from '@/components/ui/info-tip'
import epexCsvText2025 from '@/assets/epex-prices-de-2025.csv?raw'
import epexCsvText2026 from '@/assets/epex-prices-de-2026.csv?raw'
import { parseSmardCsv, parseIdaCsv, parseEpexCombinedCsv } from '@/lib/csv-prices'
import { useToast } from '@/hooks/use-toast'
import { usePools } from '@/hooks/use-pools'
import { useIncognito } from '@/hooks/use-incognito'
import { useSimulatorSessions, useDAPrice, useIntradayPrice } from '@/hooks/use-simulator'
import {
  optimizeSession,
  computeTradingDeskBaseline,
  scenarioSavingsFromBaseline,
  buildActualSlotDensity,
  blendMinPrices,
  type TradingDeskResult,
} from '@/lib/ev-optimizer'
import { SCENARIO_KEYS, type ScenarioKey, resolveIdcFullKey } from '@/lib/simulator-scenarios'
import { SavingsSummary } from '@/components/simulator/SavingsSummary'
import { SessionBreakdownTable, type SessionRow } from '@/components/simulator/SessionBreakdownTable'
import { PriceCurveChart, type SessionOverlay } from '@/components/simulator/PriceCurveChart'
import { DateStrip } from '@/components/simulator/DateStrip'
import { SavingsDailyBarsChart, type DailyRow } from '@/components/simulator/SavingsDailyBarsChart'
import { SimulatorHelpDialog } from '@/components/simulator/SimulatorHelpDialog'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { Separator } from '@/components/ui/separator'
import type { PricePoint } from '@/services/api/priceApi'
import type { components } from '../../../../types/flex-api'

type ChargingSession = components['schemas']['ChargingSession']

const DAY_MS = 86_400_000

function mergeParsed(
  a: { da: PricePoint[]; idaSeries: Record<string, PricePoint[]> },
  b: { da: PricePoint[]; idaSeries: Record<string, PricePoint[]> }
): { da: PricePoint[]; idaSeries: Record<string, PricePoint[]> } {
  const da = [...a.da, ...b.da].sort((x, y) => x.timestampMs - y.timestampMs)
  const keys = new Set([...Object.keys(a.idaSeries), ...Object.keys(b.idaSeries)])
  const idaSeries: Record<string, PricePoint[]> = {}
  for (const k of keys) {
    idaSeries[k] = [...(a.idaSeries[k] ?? []), ...(b.idaSeries[k] ?? [])].sort(
      (x, y) => x.timestampMs - y.timestampMs
    )
  }
  return { da, idaSeries }
}

const { da: BUNDLED_PRICES_RAW, idaSeries: BUNDLED_IDA_SERIES_RAW } = mergeParsed(
  parseEpexCombinedCsv(epexCsvText2025),
  parseEpexCombinedCsv(epexCsvText2026)
)
// Strip zero-price entries — the 2026 CSV uses 0.0 as a sentinel for dates not yet published.
const BUNDLED_PRICES = BUNDLED_PRICES_RAW.filter((p) => p.priceEurKwh !== 0)
const BUNDLED_IDA_SERIES_ALL = Object.fromEntries(
  Object.entries(BUNDLED_IDA_SERIES_RAW).map(([k, pts]) => [k, pts.filter((p) => p.priceEurKwh !== 0)])
)
// The simulator only compares DA, ID-Cfull, and the Cross-Market blend of the
// two — drop ID-A, ID-Cmin, ID-Cmax, ID-C1, ID-C3 entirely.
const bundledIdcFullKey = resolveIdcFullKey(BUNDLED_IDA_SERIES_ALL)
const BUNDLED_IDA_SERIES: Record<string, PricePoint[]> = bundledIdcFullKey
  ? { [bundledIdcFullKey]: BUNDLED_IDA_SERIES_ALL[bundledIdcFullKey] }
  : {}

// ── Pure helper ────────────────────────────────────────────────────────────────

function optimizeAllSessions(
  sessions: ChargingSession[] | undefined,
  prices: PricePoint[] | undefined,
  flatRate: number,
  offset: number,
  efficiency: number,
  idaSeriesMap?: Record<string, PricePoint[]>,
  crossPrices?: PricePoint[],
  idcFullKey?: string
): SessionRow[] {
  if (!sessions || !prices || sessions.length === 0) return []
  return sessions
    .map((session): SessionRow | null => {
      const targetSoC = session.desiredSoC ?? session.unpluggedSoC
      const windowEnd = session.scheduledDepartureTime ?? session.unpluggedAt
      if (session.pluggedSoC >= targetSoC) return null
      const sessionInput = {
        maxEnergy: session.maxEnergy,
        pluggedSoC: session.pluggedSoC,
        targetSoC,
        maxChargePower: session.maxChargePower,
        pluggedAt: session.pluggedAt,
        windowEnd,
      }
      const result = optimizeSession(sessionInput, prices, flatRate, offset, efficiency)
      if (result.energyKwh <= 0) return null
      const pluggedMs = new Date(session.pluggedAt).getTime()
      const unplugMs = new Date(session.unpluggedAt).getTime()

      // Trading Desk perspective trades at raw market price only — no grid
      // fees/taxes/levies — so it needs its own offset-free optimal cost.
      const tdResult = optimizeSession(sessionInput, prices, flatRate, 0, efficiency)

      // Run optimization for every available IDA series
      let idaResults: Record<string, { optimalCost: number; savings: number; savingsPct: number }> | undefined
      let idcFullOptimalCost: number | undefined
      if (idaSeriesMap) {
        const entries = Object.entries(idaSeriesMap).filter(([, pts]) => pts.length > 0)
        if (entries.length > 0) {
          idaResults = {}
          for (const [key, idaPrices] of entries) {
            const idaResult = optimizeSession(sessionInput, idaPrices, flatRate, offset, efficiency)
            if (idaResult.energyKwh > 0) {
              idaResults[key] = {
                optimalCost: idaResult.optimalCost,
                savings: idaResult.savings,
                savingsPct: idaResult.savingsPct,
              }
              if (key === idcFullKey) {
                const tdIdaResult = optimizeSession(sessionInput, idaPrices, flatRate, 0, efficiency)
                idcFullOptimalCost = tdIdaResult.optimalCost
              }
            }
          }
          if (Object.keys(idaResults).length === 0) idaResults = undefined
        }
      }

      const tdDA = computeTradingDeskBaseline(
        sessionInput, prices, efficiency, tdResult.optimalCost, tdResult.energyKwh
      )

      // Trading Desk "Optimized" scenarios: the same demand priced against
      // different market curves, all measured against ONE shared baseline —
      // DA's immediate-charge cost. Only the optimal-cost side varies between
      // scenarios, so the scenario that finds the cheapest slots always shows
      // the highest savings (Cross-Market's blended curve is pointwise ≤ both
      // DA and IDCFull, so its optimal cost is always the lowest of the three).
      const tdScenarios: Partial<Record<ScenarioKey, TradingDeskResult>> = { da: tdDA }
      if (idcFullKey && idcFullOptimalCost !== undefined) {
        tdScenarios.idcfull = scenarioSavingsFromBaseline(tdDA.baselineCost, idcFullOptimalCost)
      }
      if (crossPrices && crossPrices.length > 0) {
        const crossResult = optimizeSession(sessionInput, crossPrices, flatRate, 0, efficiency)
        if (crossResult.energyKwh > 0) {
          tdScenarios.cross = scenarioSavingsFromBaseline(tdDA.baselineCost, crossResult.optimalCost)
        }
      }

      return {
        sessionId: session.id,
        pluggedAt: session.pluggedAt,
        durationMin: Math.round((unplugMs - pluggedMs) / 60_000),
        energyKwh: result.energyKwh,
        flatCost: result.flatCost,
        optimalCost: result.optimalCost,
        savings: result.savings,
        savingsPct: result.savingsPct,
        result,
        raw: session,
        tdDA,
        tdScenarios,
        idaResults,
      }
    })
    .filter((r): r is SessionRow => r !== null)
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function SimulatorPage() {
  const {
    poolID: selectedPoolID,
    selectedDay,
    flatRate,
    offset,
    efficiency,
    view,
    isoWeek,
    isoMonth,
    setPoolID: setSelectedPoolID,
    setSelectedDay,
    setFlatRate,
    setOffset,
    setEfficiency,
    setView,
    setIsoWeek,
    setIsoMonth,
  } = useSimulatorParams()
  const [advanced, setAdvanced] = useState<boolean>(() => {
    try { return localStorage.getItem('simulator_advanced') === 'true' } catch { return false }
  })
  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>([])
  const [helpOpen, setHelpOpen] = useState(false)
  const [uploadedPrices, setUploadedPrices] = useState<PricePoint[]>([])
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadedIdaSeries, setUploadedIdaSeries] = useState<Record<string, PricePoint[]>>({})
  const [uploadedIdaFileName, setUploadedIdaFileName] = useState<string | null>(null)
  const idaFileInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  // ── View domain: the currently visible time window (day mode only) ──────────
  const [viewDomain, setViewDomain] = useState<[number, number]>(() => {
    const mid = selectedDay.getTime()
    return [mid - 12 * 3600_000, mid + 12 * 3600_000]
  })
  const [viewStart, viewEnd] = viewDomain

  // ── Period range for current view ───────────────────────────────────────────
  const { start: periodStart, end: periodEnd } = useMemo(
    () => resolveSimRange(view, selectedDay, isoWeek, isoMonth),
    [view, selectedDay, isoWeek, isoMonth]
  )

  // ── Fetch ranges ─────────────────────────────────────────────────────────────
  // Day mode:        1 range, snapped to day boundaries + padding for panning.
  // Week mode:       1 range (week in single month) or 2 ranges (week spans
  //                  month boundary) — each is a full calendar month so the
  //                  cache is shared with month-view queries.
  // Month mode:      1 range, the full calendar month.
  const fetchRanges = useMemo((): Array<{ start: Date; end: Date }> => {
    if (view !== 'day') return resolveSimFetchRange(view, selectedDay, isoWeek, isoMonth)
    const viewStartDay = startOfDay(new Date(viewStart)).getTime()
    const viewEndDay   = startOfDay(new Date(viewEnd)).getTime()
    return [{ start: new Date(viewStartDay - 2 * DAY_MS), end: new Date(viewEndDay + 3 * DAY_MS) }]
  }, [view, selectedDay, isoWeek, isoMonth, viewStart, viewEnd])

  const [range1, range2] = fetchRanges
  const hasRange2 = fetchRanges.length === 2

  const { data: pools, isLoading: poolsLoading } = usePools()
  const incognito = useIncognito()

  const eligiblePools = useMemo(
    () =>
      (pools ?? []).filter(
        (p) =>
          (p.controlConfig?.purpose === 'intraday' || p.controlConfig?.purpose === 'intraday_cloud') &&
          p.systemCount > 0
      ),
    [pools]
  )

  // Always call both sets of hooks; the second set is disabled when not needed.
  const { data: sessions1, isLoading: sessionsLoading1, error: sessionsError1 } =
    useSimulatorSessions(selectedPoolID, range1.start, range1.end)
  const { data: sessions2, isLoading: sessionsLoading2, error: sessionsError2 } =
    useSimulatorSessions(selectedPoolID, range2?.start ?? range1.start, range2?.end ?? range1.end, hasRange2)

  const { data: prices1, isLoading: pricesLoading1, error: pricesError1 } = useDAPrice(range1.start, range1.end)
  const { data: prices2, isLoading: pricesLoading2, error: pricesError2 } =
    useDAPrice(range2?.start ?? range1.start, range2?.end ?? range1.end, hasRange2)

  const { data: intraday1 } = useIntradayPrice(range1.start, range1.end)
  const { data: intraday2 } =
    useIntradayPrice(range2?.start ?? range1.start, range2?.end ?? range1.end, hasRange2)

  // Merge results from both ranges
  const sessions = useMemo(
    () => !sessions1 ? undefined : hasRange2 && sessions2 ? [...sessions1, ...sessions2] : sessions1,
    [sessions1, sessions2, hasRange2]
  )
  const prices = useMemo(
    () => !prices1 ? undefined : hasRange2 && prices2 ? [...prices1, ...prices2] : prices1,
    [prices1, prices2, hasRange2]
  )
  const intradayPrices = useMemo(
    () => !intraday1 ? undefined : hasRange2 && intraday2 ? [...intraday1, ...intraday2] : intraday1,
    [intraday1, intraday2, hasRange2]
  )

  // ── CSV price source: uploaded overrides bundled; filtered to fetch range(s) ─
  const csvPricesForRange = useMemo(() => {
    const source = uploadedPrices.length > 0 ? uploadedPrices : BUNDLED_PRICES
    const start = range1.start.getTime()
    const end   = (range2 ?? range1).end.getTime() + 1
    return source.filter((p) => p.timestampMs >= start && p.timestampMs < end)
  }, [uploadedPrices, range1, range2])

  // Merge SMARD DA with CSV (CSV wins on overlap); both needed because bundled CSV has a coverage cutoff.
  const activePrices = useMemo(() => {
    const smard = prices ?? []
    if (csvPricesForRange.length === 0) return smard
    if (smard.length === 0) return csvPricesForRange
    const merged = new Map(smard.map((p) => [p.timestampMs, p]))
    for (const p of csvPricesForRange) merged.set(p.timestampMs, p)
    return Array.from(merged.values()).sort((a, b) => a.timestampMs - b.timestampMs)
  }, [csvPricesForRange, prices])

  // IDA series filtered to the fetch range(s); uploaded overrides bundled
  const idaSeriesForRange = useMemo(() => {
    const source = Object.keys(uploadedIdaSeries).length > 0 ? uploadedIdaSeries : BUNDLED_IDA_SERIES
    const start = range1.start.getTime()
    const end   = (range2 ?? range1).end.getTime() + 1
    return Object.fromEntries(
      Object.entries(source)
        .map(([k, pts]) => [k, pts.filter((p) => p.timestampMs >= start && p.timestampMs < end)])
        .filter(([, pts]) => (pts as PricePoint[]).length > 0)
    )
  }, [uploadedIdaSeries, range1, range2])

  // Resolved key for the ID-Cfull series (aliased "IDFull" on the uploaded-CSV path)
  const idcFullKey = useMemo(() => resolveIdcFullKey(idaSeriesForRange), [idaSeriesForRange])

  // Cross-market curve: per-slot min(DA, ID-Cfull), computed once per data range —
  // never inside the per-session optimizer loop.
  const crossPricesForRange = useMemo(() => {
    return idcFullKey ? blendMinPrices(activePrices, idaSeriesForRange[idcFullKey]) : undefined
  }, [activePrices, idaSeriesForRange, idcFullKey])

  // ── Session filter bounds ───────────────────────────────────────────────────
  // Day mode filters to the panned view window; week/month includes the full period.
  const filterStart = view === 'day' ? viewStart         : periodStart.getTime()
  const filterEnd   = view === 'day' ? viewEnd           : periodEnd.getTime() + 1

  const allOptimizedRows = useMemo(
    () => optimizeAllSessions(
      sessions, activePrices, flatRate, offset, efficiency, idaSeriesForRange, crossPricesForRange, idcFullKey
    ),
    [sessions, activePrices, flatRate, offset, efficiency, idaSeriesForRange, crossPricesForRange, idcFullKey]
  )

  const sessionRows = useMemo(
    () => allOptimizedRows.filter((r) => {
      const ts = new Date(r.pluggedAt).getTime()
      return ts >= filterStart && ts < filterEnd
    }),
    [allOptimizedRows, filterStart, filterEnd]
  )

  const summary = useMemo(() => {
    const empty = {
      totalSessions: 0,
      totalEnergyKwh: 0,
      totalSavingsEur: 0,
      avgSavingsPct: 0,
      idaSeriesSummary: undefined as Record<string, { totalSavingsEur: number; avgSavingsPct: number }> | undefined,
      idaRange: undefined as { minSavingsEur: number; maxSavingsEur: number; minSavingsPct: number; maxSavingsPct: number } | undefined,
      tdDA: undefined as { totalSavingsEur: number; avgSavingsPct: number } | undefined,
      tdScenarios: undefined as Partial<Record<ScenarioKey, { totalSavingsEur: number; avgSavingsPct: number }>> | undefined,
    }
    if (sessionRows.length === 0) return empty
    const totalSessions   = sessionRows.length
    const totalEnergyKwh  = sessionRows.reduce((s, r) => s + r.energyKwh, 0)
    const totalSavingsEur = sessionRows.reduce((s, r) => s + r.savings, 0)
    const avgSavingsPct   = sessionRows.reduce((s, r) => s + r.savingsPct, 0) / totalSessions

    // Aggregate per IDA series (end-customer)
    const perSeries: Record<string, { totalSavingsEur: number; totalPct: number; count: number }> = {}
    for (const row of sessionRows) {
      if (!row.idaResults) continue
      for (const [key, res] of Object.entries(row.idaResults)) {
        if (!perSeries[key]) perSeries[key] = { totalSavingsEur: 0, totalPct: 0, count: 0 }
        perSeries[key].totalSavingsEur += res.savings
        perSeries[key].totalPct += res.savingsPct
        perSeries[key].count++
      }
    }

    const idaSeriesSummary = Object.keys(perSeries).length > 0
      ? Object.fromEntries(
          Object.entries(perSeries).map(([k, v]) => [
            k,
            { totalSavingsEur: v.totalSavingsEur, avgSavingsPct: v.totalPct / v.count },
          ])
        )
      : undefined

    // IDCFull per-session savings range
    let idaRange: typeof empty.idaRange
    const idcFullRows = sessionRows
      .map((r) => (idcFullKey ? r.idaResults?.[idcFullKey] : undefined))
      .filter((v): v is { optimalCost: number; savings: number; savingsPct: number } => v !== undefined)
    if (idcFullRows.length > 0) {
      idaRange = {
        minSavingsEur: Math.min(...idcFullRows.map((v) => v.savings)),
        maxSavingsEur: Math.max(...idcFullRows.map((v) => v.savings)),
        minSavingsPct: Math.min(...idcFullRows.map((v) => v.savingsPct)),
        maxSavingsPct: Math.max(...idcFullRows.map((v) => v.savingsPct)),
      }
    }

    // Trading Desk aggregates
    const tdDARows = sessionRows.filter((r) => r.tdDA !== undefined)
    const tdDA = tdDARows.length > 0
      ? {
          totalSavingsEur: tdDARows.reduce((s, r) => s + r.tdDA!.tdSavings, 0),
          avgSavingsPct: tdDARows.reduce((s, r) => s + r.tdDA!.tdSavingsPct, 0) / tdDARows.length,
        }
      : undefined

    // Trading Desk "Optimized" scenario aggregates (DA / IDCFull / Cross-Market)
    const perScenario: Partial<Record<ScenarioKey, { totalSavingsEur: number; totalPct: number; count: number }>> = {}
    for (const row of sessionRows) {
      if (!row.tdScenarios) continue
      for (const key of SCENARIO_KEYS) {
        const res = row.tdScenarios[key]
        if (!res) continue
        if (!perScenario[key]) perScenario[key] = { totalSavingsEur: 0, totalPct: 0, count: 0 }
        perScenario[key]!.totalSavingsEur += res.tdSavings
        perScenario[key]!.totalPct += res.tdSavingsPct
        perScenario[key]!.count++
      }
    }
    const tdScenarios = Object.keys(perScenario).length > 0
      ? Object.fromEntries(
          Object.entries(perScenario).map(([k, v]) => [
            k,
            { totalSavingsEur: v!.totalSavingsEur, avgSavingsPct: v!.totalPct / v!.count },
          ])
        ) as Partial<Record<ScenarioKey, { totalSavingsEur: number; avgSavingsPct: number }>>
      : undefined

    return { totalSessions, totalEnergyKwh, totalSavingsEur, avgSavingsPct, idaSeriesSummary, idaRange, tdDA, tdScenarios }
  }, [sessionRows, idcFullKey])

  const selectedSessions = useMemo(
    () => sessionRows.filter((r) => selectedSessionIds.includes(r.sessionId)),
    [sessionRows, selectedSessionIds]
  )

  const displaySessions = selectedSessions.length > 0 ? selectedSessions : sessionRows

  const actualDensityMap = useMemo(
    () => buildActualSlotDensity(displaySessions.map((r) => r.raw)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displaySessions]
  )

  const sessionOverlays = useMemo<SessionOverlay[]>(
    () => selectedSessions.map((s) => ({
      pluggedAtMs: new Date(s.pluggedAt).getTime(),
      unplugMs: new Date(s.pluggedAt).getTime() + s.durationMin * 60_000,
      schedule: s.result.schedule,
    })),
    [selectedSessions]
  )

  function toggleSession(row: SessionRow) {
    setSelectedSessionIds((prev) =>
      prev.includes(row.sessionId)
        ? prev.filter((id) => id !== row.sessionId)
        : [...prev, row.sessionId]
    )
  }

  // ── Daily savings aggregation (week/month bar chart) ────────────────────────
  const dailySavings = useMemo<DailyRow[]>(() => {
    if (view === 'day') return []
    const byDate = new Map<string, {
      daSavings: number
      tdScenarioSavings: Partial<Record<ScenarioKey, number>>
      sessionCount: number
    }>()
    let cursor = new Date(periodStart)
    while (cursor.getTime() <= periodEnd.getTime()) {
      byDate.set(format(cursor, 'yyyy-MM-dd'), { daSavings: 0, tdScenarioSavings: {}, sessionCount: 0 })
      cursor = addDays(cursor, 1)
    }
    for (const row of sessionRows) {
      const dateKey = format(new Date(row.pluggedAt), 'yyyy-MM-dd')
      const entry = byDate.get(dateKey)
      if (!entry) continue
      entry.daSavings += row.savings
      if (row.tdScenarios) {
        for (const key of SCENARIO_KEYS) {
          const res = row.tdScenarios[key]
          if (!res) continue
          entry.tdScenarioSavings[key] = (entry.tdScenarioSavings[key] ?? 0) + res.tdSavings
        }
      }
      entry.sessionCount++
    }
    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, { daSavings, tdScenarioSavings, sessionCount }]) => ({
        date,
        daSavings,
        tdScenarioSavings: Object.keys(tdScenarioSavings).length > 0 ? tdScenarioSavings : undefined,
        sessionCount,
      }))
  }, [view, sessionRows, periodStart, periodEnd])

  // ── Period navigation (week/month mode) ─────────────────────────────────────

  const isAtLatestPeriod = useMemo(() => {
    if (view === 'week') return startOfISOWeek(new Date()).getTime() <= periodStart.getTime()
    return format(new Date(), 'yyyy-MM') <= isoMonth
  }, [view, periodStart, isoMonth])

  function goToPrev() {
    if (view === 'week') {
      const prev = addWeeks(periodStart, -1)
      setIsoWeek(`${getISOWeekYear(prev)}-W${String(getISOWeek(prev)).padStart(2, '0')}`)
    } else {
      setIsoMonth(format(addMonths(periodStart, -1), 'yyyy-MM'))
    }
    setSelectedSessionIds([])
  }

  function goToNext() {
    if (view === 'week') {
      const next = addWeeks(periodStart, 1)
      setIsoWeek(`${getISOWeekYear(next)}-W${String(getISOWeek(next)).padStart(2, '0')}`)
    } else {
      setIsoMonth(format(addMonths(periodStart, 1), 'yyyy-MM'))
    }
    setSelectedSessionIds([])
  }

  const anySessionsLoading = (sessionsLoading1 && !sessions1) || (hasRange2 && sessionsLoading2 && !sessions2)
  const anyPricesLoading   = (pricesLoading1 && !prices1) || (hasRange2 && pricesLoading2 && !prices2)
  const isInitialLoad = anySessionsLoading || (anyPricesLoading && csvPricesForRange.length === 0)
  const error = sessionsError1 ?? sessionsError2 ?? pricesError1 ?? pricesError2

  // ── CSV upload handler ──────────────────────────────────────────────────────

  function handleCsvUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    file.text().then((text) => {
      const parsed = parseSmardCsv(text)
      if (parsed.length === 0) {
        toast({ title: 'No data parsed', description: 'Check the file is a SMARD DA price CSV.', variant: 'destructive' })
      } else {
        setUploadedPrices(parsed)
        setUploadedFileName(file.name)
        toast({ title: 'DA prices loaded', description: `${parsed.length.toLocaleString()} rows` })
      }
    })
    e.target.value = ''
  }

  function clearUpload() {
    setUploadedPrices([])
    setUploadedFileName(null)
  }

  function handleIdaCsvUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    file.text().then((text) => {
      const parsed = parseIdaCsv(text)
      // Only ID-Cfull (or its alias) is used — drop any other intraday series in the file.
      const uploadedIdcFullKey = resolveIdcFullKey(parsed)
      const result = uploadedIdcFullKey ? { [uploadedIdcFullKey]: parsed[uploadedIdcFullKey] } : {}
      const entries = Object.entries(result)
      const totalRows = entries.reduce((s, [, pts]) => s + pts.length, 0)
      if (totalRows === 0) {
        toast({ title: 'No data parsed', description: 'Check the file is a supported IDA price CSV with an ID-Cfull column.', variant: 'destructive' })
      } else {
        setUploadedIdaSeries(result)
        setUploadedIdaFileName(file.name)
        toast({
          title: 'IDA prices loaded',
          description: entries.map(([k, pts]) => `${k}: ${pts.length.toLocaleString()}`).join(' · '),
        })
      }
    })
    e.target.value = ''
  }

  function clearIdaUpload() {
    setUploadedIdaSeries({})
    setUploadedIdaFileName(null)
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full min-h-screen">
      {/* Left sidebar */}
      <aside className="w-64 shrink-0 border-r bg-muted/20 p-4 space-y-5 overflow-y-auto">
        <div>
          <div className="flex items-center gap-1.5">
            <h1 className="text-lg font-bold tracking-tight">Savings Simulator</h1>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={() => setHelpOpen(true)}
              aria-label="How the Savings Simulator works"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">DA-optimised vs flat-rate</p>
        </div>

        <Separator />

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Pool</label>
          <select
            value={selectedPoolID}
            onChange={(e) => { setSelectedPoolID(e.target.value); setSelectedSessionIds([]) }}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            disabled={poolsLoading}
          >
            <option value="">— select a pool —</option>
            {eligiblePools.map((p) => (
              <option key={p.poolID} value={p.poolID}>{incognito.poolName(p.name)}</option>
            ))}
          </select>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Only Intraday / Intraday Cloud pools with at least one system are shown.
          </p>
        </div>

        <Separator />

        <div className="space-y-4">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Parameters</p>
          <div className="space-y-2">
            <div className="flex justify-between text-xs items-center">
              <label className="font-medium flex items-center gap-1">
                Flat Rate
                <InfoTip content="The end-customer's fixed electricity tariff. Savings are calculated as: (flat rate × energy) − (optimised DA cost). Adjust this to match your customers' actual tariff." side="right" />
              </label>
              <span className="text-muted-foreground">{flatRate.toFixed(2)} €/kWh</span>
            </div>
            <Slider min={0.1} max={0.6} step={0.01} value={[flatRate]} onValueChange={([v]) => setFlatRate(v)} />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs items-center">
              <label className="font-medium flex items-center gap-1">
                Grid Offset
                <InfoTip content="Added to every market price slot to represent grid fees, taxes, and levies on top of the raw DA price. Only affects the End User perspective (flat rate vs. optimised cost) — the Trading Desk perspective trades at raw market price only." side="right" />
              </label>
              <span className="text-muted-foreground">{offset.toFixed(2)} €/kWh</span>
            </div>
            <Slider min={0.05} max={0.4} step={0.01} value={[offset]} onValueChange={([v]) => setOffset(v)} />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs items-center">
              <label className="font-medium flex items-center gap-1">
                Efficiency
                <InfoTip content="Charger round-trip efficiency. Scales the maximum energy deliverable per 15-min slot: max kWh/slot = (max power kW × 0.25 h × efficiency). Lower values mean more slots are needed to reach the target SoC." side="right" />
              </label>
              <span className="text-muted-foreground">{(efficiency * 100).toFixed(0)}%</span>
            </div>
            <Slider min={0.7} max={1.0} step={0.01} value={[efficiency]} onValueChange={([v]) => setEfficiency(v)} />
          </div>
        </div>

        <Separator />

        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Price Data</p>

          <div className="space-y-1.5">
            <p className="text-xs font-medium flex items-center gap-1">
              DA Prices
              <InfoTip content="Day-Ahead hourly auction prices (€/kWh). Used to find the cheapest slots within each session's plug-in window. Bundled data covers 2025–2026 from EPEX Spot. Upload a SMARD.de CSV to override." side="right" />
            </p>
            {uploadedFileName ? (
              <div className="flex items-center justify-between gap-2 rounded-md border bg-background px-2.5 py-1.5 text-xs">
                <span className="truncate text-foreground">{uploadedFileName}</span>
                <button onClick={clearUpload} className="shrink-0 text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {BUNDLED_PRICES.length > 0 ? 'Using bundled 2025–2026 data' : 'No bundled data loaded'}
              </p>
            )}
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed px-3 py-1.5 text-xs text-muted-foreground hover:border-foreground/40 hover:text-foreground transition-colors"
            >
              <Upload className="h-3.5 w-3.5" />
              Upload CSV
            </button>
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-medium flex items-center gap-1">
              IDA Prices
              <InfoTip content="Intraday Continuous full-session-average (ID-Cfull) price series from EPEX Spot. Feeds the Optimized IDCFull and Optimized Cross-Market Trading Desk scenarios; other columns in an uploaded file are ignored." side="right" />
            </p>
            {uploadedIdaFileName ? (
              <div className="flex items-center justify-between gap-2 rounded-md border bg-background px-2.5 py-1.5 text-xs">
                <span className="truncate text-foreground">{uploadedIdaFileName}</span>
                <button onClick={clearIdaUpload} className="shrink-0 text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {Object.values(BUNDLED_IDA_SERIES).some((pts) => pts.length > 0) ? 'Using bundled 2025–2026 data' : 'No bundled data loaded'}
              </p>
            )}
            <input ref={idaFileInputRef} type="file" accept=".csv" className="hidden" onChange={handleIdaCsvUpload} />
            <button
              onClick={() => idaFileInputRef.current?.click()}
              className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed px-3 py-1.5 text-xs text-muted-foreground hover:border-foreground/40 hover:text-foreground transition-colors"
            >
              <Upload className="h-3.5 w-3.5" />
              Upload CSV
            </button>
          </div>
        </div>
      </aside>

      {/* Right column */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* View toggle toolbar */}
        <div className="flex items-center justify-between gap-2 border-b px-4 py-2 bg-background">
          <div className="flex items-center gap-1">
            {(['day', 'week', 'month'] as const)
              .filter((v) => advanced || v !== 'month')
              .map((v) => (
                <Button
                  key={v}
                  size="sm"
                  variant={view === v ? 'default' : 'outline'}
                  onClick={() => setView(v, selectedDay)}
                  className="capitalize"
                >
                  {v === 'day' ? 'Day' : v === 'week' ? 'Week' : 'Month'}
                </Button>
              ))}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const next = !advanced
              setAdvanced(next)
              try { localStorage.setItem('simulator_advanced', String(next)) } catch { /* ignore */ }
              if (!next && view === 'month') setView('week', selectedDay)
            }}
          >
            {advanced ? 'Simple View' : 'Show Advanced'}
          </Button>
        </div>

        {/* Period navigation: DateStrip (day) or ← label → (week/month) */}
        {view === 'day' ? (
          <DateStrip
            selectedDate={selectedDay}
            onSelect={(d) => {
              const mid = d.getTime()
              setViewDomain([mid - 12 * 3600_000, mid + 12 * 3600_000])
              setSelectedDay(d)
              setSelectedSessionIds([])
            }}
          />
        ) : (
          <div className="flex items-center gap-2 border-b px-4 py-2 bg-background">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goToPrev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <PeriodGridPicker
              mode={view === 'week' ? 'week' : 'month'}
              value={view === 'week' ? isoWeek : isoMonth}
              onChange={(v) => {
                if (view === 'week') setIsoWeek(v)
                else setIsoMonth(v)
                setSelectedSessionIds([])
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={goToNext}
              disabled={isAtLatestPeriod}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        <div className="flex-1 p-6 space-y-6 overflow-y-auto">
          {!selectedPoolID && (
            <div className="rounded-lg border border-dashed py-20 text-center text-sm text-muted-foreground">
              Select a pool to run the simulation.
            </div>
          )}

          {selectedPoolID && isInitialLoad && (
            <div className="flex items-center justify-center py-20 gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading sessions and Day-Ahead prices…</span>
            </div>
          )}

          {selectedPoolID && !isInitialLoad && error && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Failed to load data</p>
                <p className="text-xs mt-0.5 opacity-80">
                  {error instanceof Error ? error.message : String(error)}
                </p>
              </div>
            </div>
          )}

          {selectedPoolID && !isInitialLoad && !error && (
            <>
              {view === 'day' ? (
                <PriceCurveChart
                  daprices={activePrices}
                  intradayPrices={intradayPrices}
                  idaSeries={idaSeriesForRange}
                  crossPrices={crossPricesForRange}
                  sessionOverlays={sessionOverlays}
                  flatRate={flatRate}
                  xDomain={viewDomain}
                  onDomainChange={(d) => { setViewDomain(d) }}
                  allSessions={displaySessions}
                  actualDensity={actualDensityMap}
                />
              ) : (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Daily Savings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SavingsDailyBarsChart
                      data={dailySavings}
                      view={view}
                    />
                  </CardContent>
                </Card>
              )}

              <SavingsSummary {...summary} advanced={advanced} />

              {view === 'day' && advanced && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Per-Session Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 pb-4 px-4">
                    <SessionBreakdownTable
                      rows={sessionRows}
                      flatRate={flatRate}
                      offset={offset}
                      selectedRows={selectedSessionIds}
                      onRowSelect={toggleSession}
                      idcFullKey={idcFullKey}
                    />
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      <SimulatorHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  )
}
